import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../contexts/i18n';
import { AppLocaleContext, useAppLocale, type LocaleContextType } from '../contexts/AppLocaleContext';
import { resourceApi } from '../services/locale/resource-api';
import AppFlashScreen, { AppFlashScreenRef } from '../components/FlashScreen/FlashScreen';

interface AppLocaleProviderProps {
    children: React.ReactNode;
    defaultLanguage?: string;
}

export const AppLocaleProvider: React.FC<AppLocaleProviderProps> = ({
    children,
    defaultLanguage = 'vi'
}) => {
    const { t } = useTranslation();
    const loaderRef = useRef<AppFlashScreenRef>(null);
    const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage);
    const [showSplash, setShowSplash] = useState(true);

    // Hàm decode HTML entities
    const decodeHTMLEntities = (text: string): string => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
    };

    // Hàm xử lý đệ quy cho object languages
    const decodeLanguageObject = (obj: any): any => {
        if (typeof obj === 'string') {
            return decodeHTMLEntities(obj);
        }
        if (Array.isArray(obj)) {
            return obj.map(item => decodeLanguageObject(item));
        }
        if (obj && typeof obj === 'object') {
            const decoded: any = {};
            for (const key in obj) {
                decoded[key] = decodeLanguageObject(obj[key]);
            }
            return decoded;
        }
        return obj;
    };

    const fetchLanguages = useCallback(async (lang?: string) => {
        const isInitialLoad = true;// !lang;
        if (isInitialLoad) {
            setShowSplash(true);
        }
        try {
            const targetLang = lang || localStorage.getItem('app_language') || defaultLanguage;

            // Fix: Add parentheses and JSON.parse
            const localeSaved = sessionStorage.getItem(`languages_${targetLang}`);
            if (localeSaved) {
                let parsedLocale = JSON.parse(localeSaved); // Parse the JSON string
                // Decode tất cả text trong languages object
                parsedLocale = decodeLanguageObject(parsedLocale);
                i18n.addResourceBundle(targetLang, 'translation', parsedLocale);
                i18n.changeLanguage(targetLang);
                return;
            }

            const langID = targetLang === 'vi' ? 1 : 2;

            const response: any = await resourceApi.getLocale(langID);

            if (response?.success) {

                let languages = response.data;

                // Decode tất cả text trong languages object
                languages = decodeLanguageObject(languages);

                localStorage.setItem('app_language', targetLang);
                // Fix: Add parentheses and JSON.stringify
                sessionStorage.setItem(`languages_${targetLang}`, JSON.stringify(languages));
                i18n.addResourceBundle(targetLang, 'translation', languages);
                i18n.changeLanguage(targetLang);
            }
        } catch (error) {
            console.error('Error fetching languages:', error);
        } finally {
            if (isInitialLoad) {
                setTimeout(() => {
                    loaderRef.current?.hide();
                    setTimeout(() => setShowSplash(false), 0);
                }, 100);
            }
        }
    }, [defaultLanguage]);

    const changeLanguage = useCallback((lang: string) => {
        // Cập nhật state + lưu lại lựa chọn
        setCurrentLanguage(lang);
        localStorage.setItem('app_language', lang);
        // Chỉ đổi ngôn ngữ sau khi đã đảm bảo bundle tồn tại (trong fetchLanguages)
        fetchLanguages(lang);
    }, [fetchLanguages]);

    useEffect(() => {
        const savedLang = localStorage.getItem('app_language') || defaultLanguage;
        setCurrentLanguage(savedLang);
        fetchLanguages();
    }, [fetchLanguages, defaultLanguage]);

    if (showSplash) {
        return <AppFlashScreen tip='' ref={loaderRef} fullscreen hasLogo duration={300} />;
    }

    const value: LocaleContextType = {
        currentLanguage,
        isLoading: showSplash,
        changeLanguage,
        fetchLanguages,
        t
    };

    return (
        <AppLocaleContext.Provider value={value}>
            {children}
        </AppLocaleContext.Provider>
    );
};

// Simple Language Selector
export const LanguageSelector: React.FC = () => {
    const { currentLanguage, changeLanguage, isLoading } = useAppLocale();

    return (
        <select
            value={currentLanguage}
            onChange={(e) => changeLanguage(e.target.value)}
            disabled={isLoading}
        >
            <option value="vi">🇻🇳 Tiếng Việt</option>
            <option value="en">🇺🇸 English</option>
        </select>
    );
};
